import { currencies } from "@/data/currencies";
import { FieldOptions, MultiFieldOptions } from "@/features/Edit/types.ts";
import i18n from "@/i18n";
import { DateValue, parseDate } from "@internationalized/date";
import { z } from "zod";

const mergeFieldOptions = (
  name: string,
  fieldOptions?: FieldOptions,
  multiFieldOptions?: MultiFieldOptions,
) => {
  const fieldOptionsOverride =
    multiFieldOptions !== undefined
      ? (multiFieldOptions[name] ?? multiFieldOptions["default"])
      : undefined;

  if (fieldOptions && fieldOptionsOverride)
    return { ...fieldOptions, ...fieldOptionsOverride };

  return fieldOptions ?? fieldOptionsOverride;
};

const isDateValue = (val: any): val is DateValue => {
  return (
    val &&
    typeof val === "object" &&
    typeof val.year === "number" &&
    typeof val.month === "number" &&
    typeof val.day === "number"
  );
};

const parseYes = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value;
  } else if (typeof value === "string") {
    return value.trim().toUpperCase() === "YES";
  } else {
    return false;
  }
};

/**
 * Convert a DateValue object to an ISO string (YYYY-MM-DD)
 * @param dateValue The DateValue object to convert
 * @returns A string in ISO format or null if the input is not a valid DateValue
 */
export const formatDateValueToISOString = (
  dateValue: unknown,
): string | null => {
  if (!dateValue) return null;

  if (isDateValue(dateValue)) {
    const { year, month, day } = dateValue;
    // Mois est 1-indexé dans DateValue mais doit être formaté avec deux chiffres
    const formattedMonth = month.toString().padStart(2, "0");
    const formattedDay = day.toString().padStart(2, "0");
    return `${year}-${formattedMonth}-${formattedDay}`;
  }

  return null;
};

const parseToDateValue = (value: unknown): DateValue | null => {
  if (typeof value === "string") {
    const datePart = value.slice(0, 10);
    return parseDate(datePart);
  } else if (isDateValue(value)) {
    return value;
  }
  return null;
};

// Define Zod schema with custom validation using the isDateValue function
const dateSchema = z
  .custom<DateValue | null>((val) => val === null || isDateValue(val), {
    message: "La date est requise.",
  })
  .nullable();

const getChangedFields = (
  original: Record<string, any>,
  updated: Record<string, any>,
) => {
  const areValuesEqual = (left: any, right: any): boolean => {
    if (Object.is(left, right)) return true;

    if (Array.isArray(left) && Array.isArray(right)) {
      if (left.length !== right.length) return false;
      return left.every((item, index) => areValuesEqual(item, right[index]));
    }

    if (
      left &&
      right &&
      typeof left === "object" &&
      typeof right === "object" &&
      left.constructor === Object &&
      right.constructor === Object
    ) {
      const leftKeys = Object.keys(left);
      const rightKeys = Object.keys(right);
      if (leftKeys.length !== rightKeys.length) return false;
      return leftKeys.every(
        (key) =>
          Object.prototype.hasOwnProperty.call(right, key) &&
          areValuesEqual(left[key], right[key]),
      );
    }

    return false;
  };

  const changedFields: Record<string, { original: any; updated: any }> = {};
  const updatedObject: Partial<typeof updated> = {};

  Object.keys(updated).forEach((key) => {
    // if the original value is different from the updated value
    if (
      !areValuesEqual(
        original[key as keyof typeof original],
        updated[key as keyof typeof updated],
      )
    ) {
      changedFields[key] = {
        original: original[key as keyof typeof original],
        updated: updated[key as keyof typeof updated],
      };
      updatedObject[key as keyof typeof updated] =
        updated[key as keyof typeof updated];
    }
  });

  return { changedFields, updatedObject };
};

const parseFloatValue = (val: unknown): number | null => {
  if (typeof val === "number") {
    return isNaN(val) ? null : val;
  }

  if (typeof val === "string") {
    const parsedVal = parseFloat(val);
    return isNaN(parsedVal) || val.trim() === "" ? null : parsedVal;
  }

  return null; // If the value is neither a number nor a string, return null
};

const parseIntValue = (val: unknown): number | null | undefined => {
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (/^-?\d+$/.test(trimmed)) return Number(trimmed);
    return undefined;
  }
  if (typeof val === "number" && !isNaN(val)) {
    return val;
  }
  return val == null ? null : undefined;
};

/**
 * Extracts the underlying schema from a Zod schema.
 * Useful for schemas that use refine, transform, superRefine, etc.
 * @param schema - The Zod schema
 * @returns The underlying schema
 */
const extractUnderlyingSchema = (schema: z.ZodTypeAny): z.ZodTypeAny => {
  let actualSchema: z.ZodTypeAny = schema;

  while (true) {
    const def = (actualSchema as any)?._def;
    if (!def) break;

    if (def.innerType) {
      actualSchema = def.innerType as z.ZodTypeAny;
      continue;
    }

    if (def.schema) {
      actualSchema = def.schema as z.ZodTypeAny;
      continue;
    }

    break;
  }

  return actualSchema;
};

/**
 * Generates default values for a Zod schema.
 * Supports schemas with refine, transform, superRefine, etc.
 * @param schema - The Zod schema
 * @param fetchedData
 * @returns An object with default values for each field in the schema
 */
const generateDefaultValues = (schema: z.ZodTypeAny, fetchedData: any) => {
  // Retrieve the underlying schema if it's a ZodEffects (refine, transform, etc.)
  const actualSchema = extractUnderlyingSchema(schema);

  if (!(actualSchema instanceof z.ZodObject)) {
    console.warn("generateDefaultValues: The schema is not a Zod object");
    return {};
  }

  const shape = actualSchema.shape; // Access the schema shape
  const defaults: Record<string, any> = {};

  Object.keys(shape).forEach((key) => {
    defaults[key] = (fetchedData && fetchedData[key]) ?? undefined;
  });

  return defaults;
};

/**
 * Transforms a currency value between display format (with symbol) and storage format (label only)
 *
 * @param val - The currency value to transform
 * @returns The transformed currency value
 */
const transformCurrency = (val: unknown): unknown => {
  if (!val) return val;

  const strVal = val as string;

  // Find the matching currency based on either the label alone or the full format (label + symbol)
  const matchedCurrency = currencies.find(
    (c) => c.label === strVal || `${c.label} (${c.symbol})` === strVal,
  );

  // If a match is found:
  // - If the input is "EUR", transform it to "EUR (€)"
  // - If the input is "EUR (€)", transform it back to "EUR"
  if (matchedCurrency) {
    return strVal === matchedCurrency.label
      ? `${matchedCurrency.label} (${matchedCurrency.symbol})`
      : matchedCurrency.label;
  }

  return strVal; // Return the input as is if no match is found
};

/**
 * Adjusts currency data when loading from API
 * Converts from display format (with symbol) to storage format (label only)
 *
 * @param data - The data object to adjust
 * @returns The adjusted data object
 */
function adjustCurrencyData(
  data: any,
  key_to_adjust: string = "currency",
): any {
  if (!data) return data;

  const newData = { ...data };

  if (newData[key_to_adjust]) {
    // Extract the currency label by removing " (symbol)"
    const matchedCurrency = currencies.find(
      (c) => `${c.label} (${c.symbol})` === newData[key_to_adjust],
    );

    // If a match is found, replace it with just the label
    if (matchedCurrency) {
      newData[key_to_adjust] = matchedCurrency.label;
    }
  }

  return newData;
}

/**
 * Adjusts currency value on form reset
 * Ensures the currency is in the correct format for display
 *
 * @param key - The field key
 * @param value - The field value
 * @returns The adjusted value
 */
function adjustCurrencyOnReset(
  key: string,
  value: unknown,
  key_to_adjust: string = "currency",
): unknown {
  if (key !== key_to_adjust) return value;

  const strVal = value as string;
  if (!strVal) return strVal;

  // Count the number of "(" and ")" in the string
  const openCount = (strVal.match(/\(/g) || []).length;
  const closeCount = (strVal.match(/\)/g) || []).length;
  const parenthesisCount = openCount + closeCount;

  // If already in display format, return as is
  if (parenthesisCount === 2) return strVal;

  // Find matching currency and convert to display format
  const matchedCurrency = currencies.find((c) => c.label === strVal);
  if (matchedCurrency) {
    return `${matchedCurrency.label} (${matchedCurrency.symbol})`;
  }

  return strVal;
}

const preprocessTextAsyncAutocompleteField = (val: any) => {
  if (val === null || val === undefined) return null;
  if (typeof val === "string") return val;
  if (typeof val === "object" && val !== null) {
    // Try different possible text properties
    if ("text" in val && val.text) return val.text;
    if ("label" in val && val.label) return val.label;
    if ("name" in val && val.name) return val.name;
    if ("value" in val && val.value) return val.value;
  }
  return null;
};

/**
 * Retrieves the translation of an asset class from available translations
 *
 * @param assetClass - The asset class value to translate
 * @param field - The asset class field (asset_class_1 or asset_class_2)
 * @param translations - The available translations object
 * @returns The translated value or the original value if no translation is available
 */
const getAssetClassTranslation = (
  assetClass: string,
  field: "asset_class_1" | "asset_class_2",
  translations?: { [key: string]: { [key: string]: string } },
): string => {
  if (!translations || !assetClass) return assetClass;

  const assetClassTranslations = translations[field] || {};
  return assetClassTranslations[assetClass] || assetClass;
};

export function coerceUtcIso(input: string): string {
  // already has 'Z' or an offset?
  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(input)) return input;
  return input.replace(" ", "T") + "Z";
}

/**
 * Formats a date string to a localized date and time format
 *
 * @param dateString - The date string to format
 * @param locale - The locale to use for formatting (default: undefined for browser's locale)
 * @returns The formatted date string or an empty string if the input is invalid
 */
const formatDate = (dateString: string, locale?: string): string => {
  if (!dateString) return "";
  const date = new Date(coerceUtcIso(dateString)); // Force UTC interpretation
  const effectiveLocale =
    locale ?? (i18n.language?.startsWith("en") ? "en" : "fr");
  return date.toLocaleString(effectiveLocale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export {
  adjustCurrencyData,
  adjustCurrencyOnReset,
  dateSchema,
  extractUnderlyingSchema,
  formatDate,
  generateDefaultValues,
  getAssetClassTranslation,
  getChangedFields,
  isDateValue,
  mergeFieldOptions,
  parseFloatValue,
  parseIntValue,
  parseToDateValue,
  parseYes,
  preprocessTextAsyncAutocompleteField,
  transformCurrency,
};
